/**
 * custom-bundle-upsell.js
 *
 * Custom bundle widget that replicates Qikify's bundle-upsell UI behaviour
 * without depending on the Qikify app at runtime.
 *
 * Responsibilities:
 *  - Renders interactive checkbox rows for addon products on the product page.
 *  - Calculates the discounted total price in real time as the buyer checks/unchecks addons.
 *  - Adds the main product + selected addons to the cart in a single /cart/add.js call,
 *    tagging each line item with _bundle_offer, _bundle_group, and _bundle_role properties
 *    so the backend Shopify Function can identify the bundle and apply the 5% discount.
 *  - Refreshes the cart drawer after a successful add without a full page reload.
 *
 * The 5% discount itself is NOT applied here — it is applied at checkout by the
 * bundle-discount Shopify Function once the cart contains a qualifying bundle group.
 */
document.addEventListener('DOMContentLoaded', () => {
  const CHECKBOX_SELECTOR = '.bundle-checkbox';
  const BUTTON_SELECTOR = '.msb-bundle-btn, .cart-bundle-btn';
  const OFFER_ROW_SELECTOR = '.msb-offer-row';
  const DEFAULT_OFFER = 'knife-care-5';
  // Must match the minAddons value stored in the discount node metafield.
  const MIN_ADDONS_FOR_DISCOUNT = 2;

  const toInteger = (value) => Number.parseInt(value, 10);
  // Each bundle add-to-cart event gets a unique group ID so the function
  // can match main + addon line items together within the same cart.
  const createBundleGroup = () => `bundle_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const formatMoney = (cents, format) => {
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '{{amount}}';
    const match = formatString.match(placeholderRegex);

    if (!match) return formatString;

    const formatWithDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number) || number === null) return '0';

      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const amount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const decimals = parts[1] ? `${decimal}${parts[1]}` : '';
      return `${amount}${decimals}`;
    };

    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
        break;
    }

    return formatString.replace(placeholderRegex, value);
  };

  const getErrorMessage = async (resp) => {
    try {
      const body = await resp.json();
      return body.description || body.message || 'Failed to add items';
    } catch {
      return 'Failed to add items';
    }
  };

  const refreshCartDrawer = async () => {
    const sidebar = document.getElementById('site-cart-sidebar');
    const cartForm = document.getElementById('AjaxCartForm');
    const cartSubtotal = document.getElementById('AjaxCartSubtotal');

    if (!sidebar || !cartForm || !cartSubtotal) {
      window.location.href = '/cart';
      return;
    }

    const response = await fetch('?section_id=helper-cart');
    if (!response.ok) {
      window.location.href = '/cart';
      return;
    }

    const text = await response.text();
    const parsed = new DOMParser().parseFromString(text, 'text/html');
    const cartFormInnerHTML = parsed.getElementById('AjaxCartForm')?.innerHTML;
    const cartSubtotalInnerHTML = parsed.getElementById('AjaxCartSubtotal')?.innerHTML;

    if (!cartFormInnerHTML || !cartSubtotalInnerHTML) {
      window.location.href = '/cart';
      return;
    }

    cartForm.innerHTML = cartFormInnerHTML;
    if (typeof cartForm.ajaxifyCartItems === 'function') {
      cartForm.ajaxifyCartItems();
    }

    cartSubtotal.innerHTML = cartSubtotalInnerHTML;

    const countNode = cartForm.querySelector('[data-cart-count]');
    const totalNode = cartForm.querySelector('[data-cart-total]');

    document.querySelectorAll('[data-header-cart-count]').forEach((elm) => {
      if (countNode) elm.textContent = countNode.textContent;
    });

    document.querySelectorAll('[data-header-cart-total]').forEach((elm) => {
      if (totalNode) elm.textContent = totalNode.textContent;
    });

    const recommendations = document.getElementById('cart-recommendations-sidebar');
    if (recommendations && typeof recommendations.generateRecommendations === 'function') {
      recommendations.generateRecommendations();
    }

    sidebar.show();
  };

  const buildItems = (rawJson, bundleOffer, bundleGroup, checkedProductIds) => {
    let parsed;
    try {
      parsed = JSON.parse(rawJson || '[]');
    } catch {
      return [];
    }

    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((item) => {
        if (item.bundleRole === 'main') return true;
        return checkedProductIds.includes(String(item.productId));
      })
      .map((item) => ({
        id: toInteger(item.id),
        quantity: toInteger(item.quantity),
        properties: {
          _bundle_offer: bundleOffer,
          _bundle_group: bundleGroup,
          _bundle_role: item.bundleRole === 'main' ? 'main' : 'addon'
        }
      }))
      .filter((item) => item.id > 0 && item.quantity > 0);
  };

  const initWidget = (widget) => {
    const button = widget.querySelector(BUTTON_SELECTOR);
    if (!button) return;

    const moneyFormat = widget.dataset.moneyFormat || '{{amount}}';
    const mainPriceCents = toInteger(widget.dataset.mainPriceCents || '0');
    const mainAvailable = widget.dataset.mainAvailable === 'true';
    // Passed from the Liquid template; should match the percent stored in the discount metafield.
    const bundleDiscountPercent = Number.parseFloat(widget.dataset.discountPercent || '0');

    const getCheckedAddonProductIds = () => {
      const ids = [];
      widget.querySelectorAll(CHECKBOX_SELECTOR).forEach((checkbox) => {
        if (
          checkbox.classList.contains('bundle-checkbox--checked') &&
          !checkbox.classList.contains('bundle-checkbox--disabled')
        ) {
          const productId = checkbox.getAttribute('data-product-id');
          if (productId) ids.push(productId);
        }
      });
      return ids;
    };

    const updateButtonState = () => {
      const hasSelection = mainAvailable && getCheckedAddonProductIds().length > 0;
      button.disabled = !hasSelection;
      button.setAttribute('aria-disabled', hasSelection ? 'false' : 'true');
    };

    // Show a strikethrough compare price only when enough addons are selected to qualify.
    const updateDisplayedTotals = () => {
      const finalEl = widget.querySelector('#bundle-final-price');
      const compareEl = widget.querySelector('#bundle-compare-price');
      if (!finalEl || !compareEl) return;

      let offerTotalCents = mainPriceCents;
      let checkedAddonCount = 0;

      widget.querySelectorAll(OFFER_ROW_SELECTOR).forEach((row) => {
        const checkbox = row.querySelector(CHECKBOX_SELECTOR);
        if (!checkbox) return;

        const isChecked = checkbox.classList.contains('bundle-checkbox--checked');
        const isDisabled = checkbox.classList.contains('bundle-checkbox--disabled');
        if (!isChecked || isDisabled) return;

        checkedAddonCount += 1;
        offerTotalCents += toInteger(row.dataset.priceCents || '0');
      });

      const hasBundleDiscount =
        checkedAddonCount >= MIN_ADDONS_FOR_DISCOUNT && bundleDiscountPercent > 0;
      const discountedCents = hasBundleDiscount
        ? Math.round((offerTotalCents * (100 - bundleDiscountPercent)) / 100)
        : offerTotalCents;

      finalEl.textContent = formatMoney(discountedCents, moneyFormat);

      // Update individual row prices to reflect bundle discount (mirrors Qikify's approach)
      widget.querySelectorAll(OFFER_ROW_SELECTOR).forEach((row) => {
        const rowPriceEl = row.querySelector('.msb-offer__price');
        const rowCompareEl = row.querySelector('.msb-offer__price--compare');
        if (!rowPriceEl || !rowCompareEl) return;

        const fullPriceCents = toInteger(row.dataset.priceCents || '0');
        const discountedPriceCents = toInteger(row.dataset.discountedPriceCents || '0');

        if (hasBundleDiscount && discountedPriceCents > 0) {
          rowPriceEl.textContent = formatMoney(discountedPriceCents, moneyFormat);
          rowCompareEl.innerHTML = `<s>${formatMoney(fullPriceCents, moneyFormat)}</s>`;
          rowCompareEl.classList.add('is-visible');
        } else {
          rowPriceEl.textContent = formatMoney(fullPriceCents, moneyFormat);
          rowCompareEl.classList.remove('is-visible');
        }
      });

      if (hasBundleDiscount) {
        compareEl.innerHTML = `<s>${formatMoney(offerTotalCents, moneyFormat)}</s>`;
        compareEl.classList.add('is-visible');
      } else {
        compareEl.classList.remove('is-visible');
        compareEl.innerHTML = '';
      }
    };

    const handleCheckboxToggle = (checkbox) => {
      if (checkbox.classList.contains('bundle-checkbox--disabled')) return;

      checkbox.classList.toggle('bundle-checkbox--checked');
      checkbox.setAttribute(
        'aria-checked',
        checkbox.classList.contains('bundle-checkbox--checked') ? 'true' : 'false'
      );

      updateButtonState();
      updateDisplayedTotals();
    };

    // POST all selected items in one request so the cart receives a complete bundle.
    // Line item properties (_bundle_*) are read by the Shopify Function to group items.
    const handleBundleClick = async () => {
      if (button.disabled) return;

      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');

      try {
        if (!mainAvailable) {
          throw new Error(
            'This bundle is unavailable because the main product cannot be added right now.'
          );
        }

        const bundleOffer = button.dataset.bundleOffer || DEFAULT_OFFER;
        const bundleGroup = createBundleGroup();
        const checkedProductIds = getCheckedAddonProductIds();
        const items = buildItems(
          button.dataset.allItemsJson,
          bundleOffer,
          bundleGroup,
          checkedProductIds
        );

        if (items.length === 0) {
          throw new Error('No items selected. Please check at least one item.');
        }

        const resp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });

        if (!resp.ok) {
          throw new Error(await getErrorMessage(resp));
        }

        await refreshCartDrawer();
      } catch (err) {
        console.error('[bundle-upsell]', err);
        alert(err && err.message ? err.message : 'Something went wrong. Please try again.');
      } finally {
        updateButtonState();
      }
    };

    widget.querySelectorAll(CHECKBOX_SELECTOR).forEach((checkbox) => {
      checkbox.addEventListener('click', () => handleCheckboxToggle(checkbox));
      checkbox.addEventListener('keydown', (event) => {
        if (event.key === ' ' || event.key === 'Enter' || event.key === 'Spacebar') {
          event.preventDefault();
          handleCheckboxToggle(checkbox);
        }
      });
    });

    button.addEventListener('click', () => {
      void handleBundleClick();
    });

    updateButtonState();
    updateDisplayedTotals();
  };

  document.querySelectorAll('.bundle-widget').forEach((widget) => {
    initWidget(widget);
  });
});
